import React from 'react';
import { ArrowLeftIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import type { ExternalApp } from '../../services/externalApps';

interface ExternalAppViewProps {
  app: ExternalApp;
  onNavigateHome: () => void;
}

/**
 * Shows a sibling app inside an iframe so this app's chrome (sidebar, theme)
 * stays visible.
 *
 * Caveats:
 * - The target must not send `X-Frame-Options` / CSP `frame-ancestors`; if it
 *   starts doing so the frame stays blank — the "open in a new tab" button is
 *   the escape hatch.
 * - Cross-origin means separate cookies/localStorage, so the embedded app may
 *   ask for its own login. Do not pass tokens via the URL.
 */
const ExternalAppView: React.FC<ExternalAppViewProps> = ({ app, onNavigateHome }) => {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-11 shrink-0 items-center justify-between gap-3 border-b dark:border-claude-darkBorder border-claude-border px-3">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={onNavigateHome}
            title="Back to chat"
            aria-label="Back to chat"
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </button>
          <span className="truncate text-sm font-medium dark:text-claude-darkText text-claude-text">
            {app.label}
          </span>
        </div>
        <button
          type="button"
          onClick={() => window.open(app.url, '_blank', 'noopener')}
          title="Open in a new tab"
          aria-label="Open in a new tab"
          className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-lg px-2 text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          Open in new tab
        </button>
      </div>
      <iframe
        src={app.url}
        title={app.label}
        className="w-full flex-1 border-0 dark:bg-claude-darkBg bg-claude-bg"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="camera; microphone; clipboard-read; clipboard-write; fullscreen"
      />
    </div>
  );
};

export default ExternalAppView;
